import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faCheck } from "@fortawesome/free-solid-svg-icons";
import ControlHorariLayout from "./ControlHorariLayout";
import { chFetch, ChSessioCaducadaError } from "./chAuth";
import { chRegistrarEvent } from "./chFitxatge";
import { chLlistarPendents } from "./chIndexedDB";
import { chDesarEstatCache, chLlegirEstatCache } from "./chCache";
import { chFusionarEventsAmbPendents, chEstatDesdeEvents } from "./chEstat";
import { useChEstatConnexio, chComprovarApiAccessible } from "./chConnexio";
import "./controlhorari.css";

function formatHoraViva(data) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(data.getHours())}:${pad(data.getMinutes())}:${pad(data.getSeconds())}`;
}

// Selector de tipus de pausa (CURRENT_TASK_SIMPLIFICAR_FLUX_FITXATGE.md):
// la pantalla "Què vols registrar?" ja no s'usa. S'arriba aquí des d'Inici
// en prémer "Inici de pausa". El fitxatge en si continua sent chRegistrarEvent.
function ControlHorariFitxar() {
  const [estatServidor, setEstatServidor] = useState(null);
  const [pendents, setPendents] = useState([]);
  const [carregant, setCarregant] = useState(true);
  const [pausaSeleccionada, setPausaSeleccionada] = useState(null);
  const [enviant, setEnviant] = useState(false);
  const [error, setError] = useState("");
  const [ara, setAra] = useState(new Date());
  const [servidorInaccessible, setServidorInaccessible] = useState(false);
  const { connectat: enLinia } = useChEstatConnexio();
  const navigate = useNavigate();

  // Comprovació real i puntual (no periòdica) contra el backend: només en
  // entrar a aquesta pantalla, per poder avisar si hi ha xarxa però el
  // servidor no respon. El fitxatge en si no depèn d'aquest resultat: sempre
  // s'intenta igualment i, si falla, es queda a la cua local (chFitxatge.js).
  useEffect(() => {
    let vigent = true;
    if (enLinia) {
      chComprovarApiAccessible().then((ok) => {
        if (vigent) setServidorInaccessible(!ok);
      });
    }
    return () => {
      vigent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarPendents = useCallback(async () => {
    try {
      setPendents(await chLlistarPendents());
    } catch {
      // IndexedDB no disponible: es continua només amb el que digui el servidor.
    }
  }, []);

  const carregarEstat = useCallback(async () => {
    try {
      const resposta = await chFetch("/ControlHorariEstat.php");
      const dades = await resposta.json();
      if (!dades.success) {
        setError(dades.message || "No s'ha pogut carregar l'estat.");
        return;
      }
      setEstatServidor(dades);
      setError("");
      chDesarEstatCache(dades);
    } catch (err) {
      if (err instanceof ChSessioCaducadaError) {
        navigate("/Control_Horari/login", { replace: true });
        return;
      }
      const cache = chLlegirEstatCache();
      if (cache?.estat) {
        setEstatServidor(cache.estat);
        setError("");
      } else {
        setError("No s'ha pogut connectar amb el servidor i encara no hi ha cap dada guardada.");
      }
    } finally {
      setCarregant(false);
    }
  }, [navigate]);

  useEffect(() => {
    carregarPendents();
    carregarEstat();
  }, [carregarPendents, carregarEstat]);

  useEffect(() => {
    const interval = setInterval(() => setAra(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const eventsFusionats = useMemo(
    () => chFusionarEventsAmbPendents(estatServidor?.events, pendents),
    [estatServidor, pendents]
  );
  const { transicionsPermeses } = useMemo(() => chEstatDesdeEvents(eventsFusionats), [eventsFusionats]);
  const tipusPausa = estatServidor?.tipusPausa || [];

  useEffect(() => {
    if (carregant) return;
    if (!transicionsPermeses.includes("INICI_PAUSA")) {
      navigate("/Control_Horari/avui", { replace: true });
    }
  }, [carregant, transicionsPermeses, navigate]);

  const registrar = async (tipusEvent, idTipusPausa = null, nomTipusPausa = null) => {
    if (enviant) return;
    setEnviant(true);
    setError("");
    try {
      await chRegistrarEvent(tipusEvent, idTipusPausa, nomTipusPausa);
      navigate("/Control_Horari/avui", { replace: true });
    } catch (err) {
      if (err instanceof ChSessioCaducadaError) {
        navigate("/Control_Horari/login", { replace: true });
        return;
      }
      setError(err.message || "No s'ha pogut registrar l'event.");
      setEnviant(false);
    }
  };

  if (carregant) {
    return (
      <ControlHorariLayout titol="Fitxar" ajuda="tipusPausa">
        <div className="ch-carregant">Carregant...</div>
      </ControlHorariLayout>
    );
  }

  if (!transicionsPermeses.includes("INICI_PAUSA")) {
    return (
      <ControlHorariLayout titol="Fitxar" ajuda="tipusPausa">
        <div className="ch-carregant">Carregant...</div>
      </ControlHorariLayout>
    );
  }

  const nomPausaSeleccionada = tipusPausa.find((tp) => tp.idTipusPausaEmpleats === pausaSeleccionada)?.Nom || null;
  return (
    <ControlHorariLayout titol="Fitxar" ajuda="tipusPausa">
      <div className="ch-fitxar">
        <p className="ch-pregunta">Què vols registrar?</p>
        <p className="ch-subpregunta">Selecciona el tipus de pausa</p>
        <p className="ch-rellotge-petit">{formatHoraViva(ara)}</p>

        {error && (
          <div className="ch-avis-error">
            <FontAwesomeIcon icon={faCircleExclamation} /> {error}
          </div>
        )}

        {!enLinia && <p className="ch-avis-offline">Sense connexió: es registrarà en aquest dispositiu i se sincronitzarà més tard.</p>}
        {enLinia && servidorInaccessible && (
          <p className="ch-avis-offline">
            El servidor no respon en aquest moment: es registrarà en aquest dispositiu i se sincronitzarà quan torni a
            estar disponible.
          </p>
        )}

        {tipusPausa.length === 0 && <p className="ch-text-suau">No hi ha Tipus de pausa configurats per aquesta Empresa.</p>}

        <div className="ch-llista-pausa">
          {tipusPausa.map((tp) => (
            <button
              type="button"
              key={tp.idTipusPausaEmpleats}
              className={`ch-opcio-pausa${pausaSeleccionada === tp.idTipusPausaEmpleats ? " seleccionada" : ""}`}
              onClick={() => setPausaSeleccionada(tp.idTipusPausaEmpleats)}
              disabled={enviant}
            >
              <span className="ch-opcio-pausa-text">
                <strong>{tp.Nom}</strong>
                <span>{tp.ComputaTempsTreball ? "Computa com a temps de treball" : "No computa com a temps de treball"}</span>
              </span>
              {pausaSeleccionada === tp.idTipusPausaEmpleats && <FontAwesomeIcon icon={faCheck} />}
            </button>
          ))}
        </div>

        <div className="ch-accio-principal">
          <button
            type="button"
            className="ch-boto-principal ch-boto-blau"
            onClick={() => registrar("INICI_PAUSA", pausaSeleccionada, nomPausaSeleccionada)}
            disabled={!pausaSeleccionada || enviant}
          >
            {enviant ? "Registrant..." : "INICIAR PAUSA"}
          </button>
          <button
            type="button"
            className="ch-boto-secundari"
            onClick={() => navigate("/Control_Horari/avui")}
            disabled={enviant}
          >
            CANCEL·LAR
          </button>
        </div>
      </div>
    </ControlHorariLayout>
  );
}

export default ControlHorariFitxar;
